import type { Ref } from 'react';

import type { MutualFundOverview } from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DetailExternalLink } from '#/components/detail/DetailExternalLink';
import { DetailPrintButton } from '#/components/detail/DetailPrintButton';
import { TickerPriceSummary } from '#/components/detail/TickerPriceSummary';
import { PageTitle } from '#/components/ui/PageTitle';
import { Ticker } from '#/components/ui/Ticker';
import { Updated } from '#/components/ui/Updated';
import { formatCompactCurrency, formatMonthDayYearDate } from '#/utils/format';
import { getMutualFundPriceChangeDisplay } from '#/utils/priceChange';

interface MutualFundSummarySectionProps {
  data: MutualFundOverview | null | undefined;
  id: string;
  priceSummaryRef?: Ref<HTMLDivElement>;
}

export function MutualFundSummarySection({
  data,
  id,
  priceSummaryRef,
}: MutualFundSummarySectionProps) {
  if (!data) {
    return null;
  }

  const symbol = data.symbol;
  const fundName = data.name ?? id;

  const { percentChange, valueChange, periodLabel } =
    getMutualFundPriceChangeDisplay(data);

  return (
    <header id="summary" className="flow flow-20 scroll-mt-(--scroll-offset)">
      <div className="text-static-14 flex items-center gap-4">
        <Ticker symbol={symbol} />
        <span>Mutual Fund, USD</span>
      </div>

      <PageTitle flush>{fundName}</PageTitle>

      {data.last_refreshed_timestamp && (
        <Updated
          date={data.last_refreshed_timestamp}
          variant="time"
          className="flow-8"
        />
      )}

      <div className="flex flex-col items-start justify-center gap-16 md:flex-row md:items-center md:justify-between">
        <TickerPriceSummary
          ref={priceSummaryRef}
          price={data.current_price}
          percentChange={percentChange}
          valueChange={valueChange}
          valueChangeSuffix={` ${periodLabel}`}
        />
        <DetailPrintButton
          dataTrack="detail_download_pdf"
          dataTrackContext="mutual_fund_detail"
        />
      </div>

      {(data.fund_description || data.prospectus_url) && (
        <div className="flow flow-inner-8 text-fluid-base-sm">
          {data.fund_description && (
            <p data-testid="fund-description">{data.fund_description}</p>
          )}
          {data.prospectus_url && (
            <DetailExternalLink
              href={data.prospectus_url}
              dataTrack="detail_fund_prospectus"
              dataTrackLabel="Fund Prospectus"
              dataTrackContext="mutual_fund_summary"
            >
              Fund Prospectus
            </DetailExternalLink>
          )}
        </div>
      )}

      <div className="text-static-14 border-blue-30 grid grid-cols-2 gap-y-20 border-t pt-20 md:grid-cols-3">
        <div>
          <p className="font-semibold">Net Assets</p>
          <p>{formatCompactCurrency(data.net_assets) ?? '—'}</p>
        </div>
        <div>
          <p className="font-semibold">Inception Date</p>
          <p>{formatMonthDayYearDate(data.inception_date) ?? '—'}</p>
        </div>
        <div>
          <p className="font-semibold">Advisor(s)</p>
          <p>{data.advisors ?? '—'}</p>
        </div>
      </div>
    </header>
  );
}
